import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Save, ServerCog, Activity, KeyRound, ShieldAlert, Gauge } from "lucide-react";
import { useAuth, apiError } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

function Field({ label, value, onChange, testid, textarea, hint }) {
  return (
    <div className="border-r border-b border-wr-border p-4 bg-wr-surface">
      <label className="wr-label">{label}</label>
      {hint && <p className="text-[0.68rem] text-wr-muted mt-0.5">{hint}</p>}
      {textarea ? (
        <Textarea
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="mt-2 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0"
        />
      ) : (
        <Input
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="mt-2 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0"
        />
      )}
    </div>
  );
}

export default function Settings() {
  const { user, logout, loadMe } = useAuth();
  const navigate = useNavigate();
  const [founder, setFounder] = useState({});
  const [company, setCompany] = useState({});
  const [workspace, setWorkspace] = useState({});
  const [model, setModel] = useState(null);
  const [usage, setUsage] = useState(null);
  const [intake, setIntake] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ceiling, setCeiling] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [privacyReqs, setPrivacyReqs] = useState([]);
  const [privacyBusy, setPrivacyBusy] = useState(false);
  const [connectors, setConnectors] = useState([]);
  const [billing, setBilling] = useState(null);

  const loadPrivacy = async () => {
    try {
      const { data } = await api.get("/privacy/requests");
      setPrivacyReqs(data);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [{ data: prof }, { data: ms }, { data: us }, { data: ik }, { data: cl }] = await Promise.all([
          api.get("/workspace/profile"),
          api.get("/settings/model-status"),
          api.get("/settings/usage"),
          api.get("/intake/status"),
          api.get("/settings/usage-ceiling"),
        ]);
        setFounder(prof.founder || {});
        setCompany(prof.company || {});
        setWorkspace(prof.workspace || {});
        setModel(ms);
        setUsage(us);
        setIntake(ik);
        setCeiling(cl);
        loadPrivacy();
        try {
          const { data: cn } = await api.get("/connectors");
          setConnectors(cn);
        } catch (e) { /* ignore */ }
        try {
          const { data: bl } = await api.get("/billing/me");
          setBilling(bl);
        } catch (e) { /* ignore */ }
      } catch (e) {
        toast.error(apiError(e));
      }
    })();
  }, []);

  const changePassword = async () => {
    if (pw.new_password !== pw.confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password changed. Other sessions were signed out.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPwBusy(false);
    }
  };

  const requestPrivacy = async (kind) => {
    setPrivacyBusy(true);
    try {
      const { data } = await api.post("/privacy/request", { kind, note: "" });
      toast.success(data.message);
      loadPrivacy();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPrivacyBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/workspace/profile", {
        founder_name: founder.founder_name,
        primary_objective: founder.primary_objective,
        operating_constraints: founder.operating_constraints,
        urgent_issue: founder.urgent_issue,
        company_name: company.company_name,
        industry: company.industry,
        business_stage: company.business_stage,
        primary_offer: company.primary_offer,
        primary_customer: company.primary_customer,
        business_model: company.business_model,
        team_size: company.team_size,
        timezone: workspace.timezone,
      });
      await api.put("/workspace/settings", { name: workspace.name, timezone: workspace.timezone });
      await loadMe();
      toast.success("Settings saved.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-6 sm:py-8 pb-28">
      <p className="wr-label !text-wr-red">▸ SARGE · BUSINESS PROFILE</p>
      <h1 className="wr-heading font-bold text-white text-2xl sm:text-3xl mt-2">Business Profile.</h1>

      {intake && (
        <div className="mt-5 border border-wr-border bg-wr-surface p-4" data-testid="profile-completeness">
          <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
            <span className="wr-label">What Sarge knows · {intake.completeness}% complete</span>
            <Button onClick={() => navigate("/intake")} data-testid="continue-intake"
              className="rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider text-xs h-9">
              {intake.completeness >= 100 ? "Update Business Profile" : "Continue Business Intake"}
            </Button>
          </div>
          <div className="h-1.5 bg-wr-surface2 overflow-hidden">
            <div className="h-full bg-wr-red transition-all duration-500" style={{ width: `${intake.completeness}%` }} />
          </div>
          {intake.missing_areas?.length > 0 && (
            <p className="text-xs text-wr-muted mt-2">Still to cover: {intake.missing_areas.join(" · ")}</p>
          )}
        </div>
      )}

      {/* Status cards */}
      <div className="mt-6 border-t border-l border-wr-border grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <div className="border-r border-b border-wr-border p-4 bg-wr-surface" data-testid="settings-model-status">
          <span className="wr-label flex items-center gap-2"><ServerCog size={13} className="text-wr-red" /> Model</span>
          {model ? (
            <>
              <p className={`wr-mono text-sm mt-2 ${model.configured ? "text-emerald-400" : "text-wr-red"}`}>
                {model.configured ? "Configured" : "Not configured"}
              </p>
              <p className="text-xs text-wr-muted mt-1">{model.provider} · {model.model}</p>
            </>
          ) : (
            <p className="wr-mono text-sm mt-2 text-wr-muted">…</p>
          )}
        </div>
        <div className="border-r border-b border-wr-border p-4 bg-wr-surface" data-testid="settings-usage">
          <span className="wr-label flex items-center gap-2"><Activity size={13} className="text-wr-red" /> Usage</span>
          <p className="wr-mono text-sm mt-2 text-white">{usage?.requests ?? 0} requests</p>
          <p className="text-xs text-wr-muted mt-1">{usage?.prompt_version}</p>
        </div>
        <div className="border-r border-b border-wr-border p-4 bg-wr-surface" data-testid="settings-usage-ceiling">
          <span className="wr-label flex items-center gap-2"><Gauge size={13} className="text-wr-red" /> AI Usage (month)</span>
          {ceiling ? (
            <>
              <p className={`wr-mono text-sm mt-2 ${ceiling.exceeded ? "text-wr-red" : ceiling.warning ? "text-amber-400" : "text-white"}`}>
                {ceiling.used} / {ceiling.limit}
              </p>
              <div className="h-1 bg-wr-surface2 overflow-hidden mt-2">
                <div className={`h-full transition-all ${ceiling.exceeded ? "bg-wr-red" : ceiling.warning ? "bg-amber-400" : "bg-emerald-400"}`} style={{ width: `${Math.min(100, ceiling.percent)}%` }} />
              </div>
              {ceiling.warning && <p className="text-[0.68rem] text-amber-400 mt-1.5" data-testid="usage-warning">Approaching your monthly limit.</p>}
              {ceiling.exceeded && <p className="text-[0.68rem] text-wr-red mt-1.5" data-testid="usage-exceeded">Limit reached — contact your operator.</p>}
            </>
          ) : (
            <p className="wr-mono text-sm mt-2 text-wr-muted">…</p>
          )}
        </div>
        <div className="border-r border-b border-wr-border p-4 bg-wr-surface" data-testid="settings-workspace-id">
          <span className="wr-label">Workspace</span>
          <p className="wr-mono text-sm mt-2 text-white truncate">{workspace.name}</p>
          <p className="text-xs text-wr-muted mt-1 truncate">{workspace.id}</p>
        </div>
      </div>

      {/* Security */}
      <h2 className="wr-label mt-8 mb-3 flex items-center gap-2"><KeyRound size={13} className="text-wr-red" /> Security</h2>
      <div className="border border-wr-border bg-wr-surface p-4 sm:p-5" data-testid="security-section">
        <p className="text-xs text-wr-muted">Change your password. This signs out every other device.</p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input type="password" placeholder="Current password" data-testid="pw-current"
            value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
            className="bg-wr-bg border-wr-border rounded-none focus-visible:ring-0" />
          <Input type="password" placeholder="New password (min 8)" data-testid="pw-new"
            value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
            className="bg-wr-bg border-wr-border rounded-none focus-visible:ring-0" />
          <Input type="password" placeholder="Confirm new password" data-testid="pw-confirm"
            value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
            className="bg-wr-bg border-wr-border rounded-none focus-visible:ring-0" />
        </div>
        <Button onClick={changePassword} disabled={pwBusy || !pw.current_password || !pw.new_password}
          data-testid="pw-submit"
          className="mt-4 rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider h-10 text-xs disabled:opacity-50">
          {pwBusy ? "Updating…" : "Change password"}
        </Button>
      </div>

      {/* Data & privacy */}
      <h2 className="wr-label mt-8 mb-3 flex items-center gap-2"><ShieldAlert size={13} className="text-wr-red" /> Data &amp; Privacy</h2>
      <div className="border border-wr-border bg-wr-surface p-4 sm:p-5" data-testid="privacy-section">
        <p className="text-xs text-wr-muted">
          Request an export of your workspace data or deletion of your account. During the private beta these are actioned manually by your operator — nothing is deleted automatically.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button onClick={() => requestPrivacy("export")} disabled={privacyBusy} variant="outline"
            data-testid="privacy-export"
            className="rounded-none border-wr-border bg-transparent text-wr-sub hover:text-white hover:bg-wr-surface2 wr-mono uppercase tracking-wider h-10 text-xs">
            Request data export
          </Button>
          <Button onClick={() => requestPrivacy("deletion")} disabled={privacyBusy} variant="outline"
            data-testid="privacy-deletion"
            className="rounded-none border-wr-red/40 bg-transparent text-wr-red hover:bg-wr-redMuted wr-mono uppercase tracking-wider h-10 text-xs">
            Request account deletion
          </Button>
        </div>
        {privacyReqs.length > 0 && (
          <div className="mt-4 space-y-1.5" data-testid="privacy-requests-list">
            {privacyReqs.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2 border-b border-wr-border py-1.5 text-xs" data-testid={`privacy-req-${r.id}`}>
                <span className="text-white capitalize">{r.kind}</span>
                <span className="wr-mono text-[0.62rem] uppercase text-wr-muted">{r.status}</span>
                <span className="wr-mono text-[0.62rem] text-wr-muted ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        )}
        <a href="/legal/privacy" target="_blank" rel="noreferrer" className="wr-label hover:text-white inline-block mt-4" data-testid="privacy-legal-link">
          Read the Privacy Policy ↗
        </a>
      </div>

      {/* Subscription */}
      {billing?.has_subscription && (
        <>
          <h2 className="wr-label mt-8 mb-3">Subscription</h2>
          <div className="border border-wr-border bg-wr-surface p-4 sm:p-5 flex items-center justify-between gap-3 flex-wrap" data-testid="billing-section">
            <div>
              <p className="text-sm text-white capitalize" data-testid="billing-status">
                {billing.plan === "sarge_founding_annual" ? "Annual ($1,490/yr)" : "Monthly ($149/mo)"} · {billing.status}
                {billing.cancel_at_period_end ? " · cancels at period end" : ""}
              </p>
              {billing.current_period_end && (
                <p className="text-xs text-wr-sub mt-0.5" data-testid="billing-renewal">
                  {billing.cancel_at_period_end ? "Access until" : "Renews"} {new Date(billing.current_period_end * 1000).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                </p>
              )}
              {billing.payment_state && ["refunded", "disputed", "dispute_closed"].includes(billing.payment_state) && (
                <p className="text-xs text-amber-400 mt-0.5 capitalize" data-testid="billing-payment-state">Payment status: {billing.payment_state.replace("_", " ")}</p>
              )}
              {billing.status === "past_due" && (
                <p className="text-xs text-wr-red mt-0.5" data-testid="billing-past-due">Payment failed — update your card in the portal to keep access.</p>
              )}
              <p className="text-xs text-wr-muted mt-0.5">Manage your payment method, invoices, plan and cancellation in the secure Stripe portal.</p>
            </div>
            <Button
              data-testid="billing-portal"
              onClick={async () => {
                try { const { data } = await api.post("/billing/portal", {}); window.location.href = data.portal_url; }
                catch (e) { toast.error(apiError(e)); }
              }}
              className="rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider h-10 text-xs">
              Manage subscription
            </Button>
          </div>
        </>
      )}

      {/* Connections */}
      <h2 className="wr-label mt-8 mb-3">Connections (read-only)</h2>
      <div className="border border-wr-border bg-wr-surface p-4 sm:p-5" data-testid="connectors-section">
        <p className="text-xs text-wr-muted">Optional read-only business signals. These never send, reply, or change anything in your accounts.</p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {connectors.map((c) => (
            <div key={c.id} className="border border-wr-border p-3 flex items-center justify-between gap-3" data-testid={`connector-${c.id}`}>
              <div className="min-w-0">
                <p className="text-sm text-white">{c.name}{c.restricted && !c.generally_available ? <span className="wr-mono text-[0.55rem] uppercase text-amber-400 ml-2">restricted · test users only</span> : null}</p>
                <p className="text-[0.68rem] text-wr-muted mt-0.5">{c.description}</p>
                {c.status_reason && <p className="text-[0.62rem] text-wr-muted mt-1">{c.status_reason}</p>}
              </div>
              <span className={`wr-mono text-[0.6rem] uppercase px-2.5 py-1 border shrink-0 ${c.status === "Connected" ? "border-emerald-500/40 text-emerald-400" : c.status === "Error" || c.status === "Reauthorization Required" ? "border-wr-red/40 text-wr-red" : c.status === "Verification Pending" ? "border-amber-500/40 text-amber-400" : "border-wr-border text-wr-muted"}`} data-testid={`connector-status-${c.id}`}>
                {c.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Workspace */}
      <h2 className="wr-label mt-8 mb-3">Workspace</h2>
      <div className="border-t border-l border-wr-border grid grid-cols-1 sm:grid-cols-2">
        <Field label="Workspace name" testid="set-ws-name" value={workspace.name} onChange={(v) => setWorkspace({ ...workspace, name: v })} />
        <Field label="Timezone" testid="set-ws-tz" value={workspace.timezone} onChange={(v) => setWorkspace({ ...workspace, timezone: v })} hint="e.g. America/New_York" />
      </div>

      {/* Founder */}
      <h2 className="wr-label mt-8 mb-3">Founder profile</h2>
      <div className="border-t border-l border-wr-border grid grid-cols-1 sm:grid-cols-2">
        <Field label="Founder name" testid="set-founder-name" value={founder.founder_name} onChange={(v) => setFounder({ ...founder, founder_name: v })} />
        <Field label="Primary objective" testid="set-objective" value={founder.primary_objective} onChange={(v) => setFounder({ ...founder, primary_objective: v })} />
        <Field label="Operating constraints" testid="set-constraints" textarea value={founder.operating_constraints} onChange={(v) => setFounder({ ...founder, operating_constraints: v })} />
        <Field label="Most urgent unresolved issue" testid="set-urgent" textarea value={founder.urgent_issue} onChange={(v) => setFounder({ ...founder, urgent_issue: v })} />
      </div>

      {/* Company */}
      <h2 className="wr-label mt-8 mb-3">Company profile</h2>
      <div className="border-t border-l border-wr-border grid grid-cols-1 sm:grid-cols-2">
        <Field label="Company name" testid="set-company" value={company.company_name} onChange={(v) => setCompany({ ...company, company_name: v })} />
        <Field label="Industry" testid="set-industry" value={company.industry} onChange={(v) => setCompany({ ...company, industry: v })} />
        <Field label="Business stage" testid="set-stage" value={company.business_stage} onChange={(v) => setCompany({ ...company, business_stage: v })} />
        <Field label="Primary offer" testid="set-offer" value={company.primary_offer} onChange={(v) => setCompany({ ...company, primary_offer: v })} />
        <Field label="Primary customer" testid="set-customer" value={company.primary_customer} onChange={(v) => setCompany({ ...company, primary_customer: v })} />
        <Field label="Business model" testid="set-model" value={company.business_model} onChange={(v) => setCompany({ ...company, business_model: v })} />
        <Field label="Team size" testid="set-team" value={company.team_size} onChange={(v) => setCompany({ ...company, team_size: v })} />
      </div>

      <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
        <Button onClick={save} disabled={busy} data-testid="settings-save" className="rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider h-11 px-6">
          <Save size={15} className="mr-2" /> {busy ? "Saving…" : "Save changes"}
        </Button>
        <Button onClick={doLogout} variant="outline" data-testid="settings-logout" className="rounded-none border-wr-border bg-transparent text-wr-sub hover:text-wr-red hover:bg-wr-surface2 wr-mono uppercase tracking-wider h-11">
          <LogOut size={15} className="mr-2" /> Sign out
        </Button>
      </div>
    </div>
  );
}
